/**
 * Singbac — Incorporation Post chatflow (SG, Singbac 문서셋 · 7종).
 * Studio: title → 엔티티(설립 완료 회사) 선택(자동바인딩) → confirm + live document package.
 */
import type { ConversationMessage } from '@/features/AI-documents/types/docGen.types';
import type {
  ChatFlow,
  ChatFlowContext,
  ChatFlowEventResult,
} from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';

export const singbacIncorporationPostFlow: ChatFlow = {
  id: 'singbac-incorporation-post',
  taskCodeMatch: [],
  categoryMatch: ['singbac incorporation post', 'singbac', 'incorporation post'],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `Welcome to **Singbac — Incorporation Post**.\n\n` +
          `This preset prepares the post-incorporation / resident-director document package` +
          (docCount > 0 ? ` (${docCount} template(s) linked)` : '') +
          `: DRIW First Board Resolutions, Nominee Director Service & Indemnity Agreement, ` +
          `NON NOMINEE Notice, Notice by Nominee Director, Undertaking by Client, ` +
          `Disclosure of Interest (s.165 & s.156), and Company's Structure Chart.\n\n` +
          `Select the client's **incorporated company** to auto-fill the company details, then ` +
          `fill the client / resident-director fields on the right. Click **✨** on a name to ` +
          `look up a **Contact**, and **📍** for addresses.\n\n` +
          `First, please enter a **Task Title**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: { targetVarKey: '_taskTitle', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      // 설립 후(POST) — 이미 등록된 회사를 고른다. T-Link/L_INCORPORATIONS(설립 진행 데이터) 가 아니라
      // 일반 프리셋과 동일한 기존 등록회사(ENTITY) 선택기.
      aiMessage:
        'Search and select the client\'s **registered company** to auto-fill the company details, or skip and fill manually.',
      widget: 'entity-selector',
      // 엔티티 없이도 진행 — "Skip — fill documents manually" 버튼(entity-skip) 노출
      widgetConfig: { targetVarKey: '_entity', targetEntityIndex: 0, allowSkip: true },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Ready. Complete the documents on the right — add NON NOMINEE / Disclosure copies per client-director as needed. ' +
        'Click **Generate** when ready.',
      widget: null,
      nextStep: null,
    },
    {
      // 커스텀 폼 소스 선택기 — 퀵버튼("Custom form data")/회사명 버튼으로 진입.
      id: 'select-form',
      aiMessage:
        'Select a **custom form** and one of its **submitted responses** — then drag any value onto a document field.',
      widget: 'custom-form-picker',
      widgetConfig: { targetVarKey: '_customForm', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      // 선택한 응답의 key:value 드래그 팔레트 (자동바인딩 없음 — 드래그앤드롭 전용).
      id: 'custom-form-palette',
      aiMessage: 'Drag any value below onto a document field to fill it.',
      widget: 'custom-form-data',
      nextStep: null,
    },
    {
      id: 'officer-role',
      aiMessage: 'Please select a role for the person you want to look up.',
      widget: 'officer-role-buttons',
      awaitEvent: true,
    },
    {
      id: 'officer-list',
      aiMessage: 'Select a person from the list.',
      widget: 'officer-list',
      awaitEvent: true,
    },
    {
      id: 'officer-detail',
      aiMessage: 'Person details loaded. The form has been pre-filled.',
      widget: 'officer-field-picker',
      awaitEvent: true,
    },
    {
      id: 'address-input',
      aiMessage: 'Enter the address below.',
      widget: 'address-input',
      awaitEvent: true,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (
      (eventName === 'value-submit' || eventName === 'text-input-submit') &&
      context.currentStepId === 'input-title'
    ) {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { _taskTitle: String(title).trim(), taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (
      (eventName === 'value-submit' || eventName === 'skip' || eventName === 'entity-skip') &&
      (context.currentStepId === 'select-entity' || context.currentStepId === 'pick-company')
    ) {
      return {
        messages: [
          {
            role: 'user',
            content: 'Skipped company linking. Fill documents manually.',
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          entityCd: '',
          icpCd: '',
          entityName: '',
          company_name: '',
          registrationNo: '',
          tlinkData: null,
          entitySkipped: true,
        },
        nextStepId: 'confirm',
      };
    }

    // POST — 기존 등록회사(ENTITY) 선택. 패널이 entityCd 를 watch 해 회사 데이터를 자동 채운다.
    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = String(ent.entityCd || ent.icpCd || '').trim();
      const entityName = String(ent.entityName || ent.companyName || 'Entity').trim();
      if (!entityCd) return {};
      return {
        messages: [
          {
            role: 'user',
            content: `Selected company: **${entityName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          entityCd,
          icpCd: entityCd,
          entityName,
          company_name: entityName,
          registrationNo: ent.registrationNo || ent.regNo || '',
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const key = String(ctx.key || '');
      const scopedKey = String(ctx.scopedKey || key || '').trim();

      if (
        key === 'company_name' ||
        key === 'entity_name' ||
        ctx.lookupType === 'incorporation' ||
        (ctx.fieldGroup === 'singbac-incorporation-post' &&
          !ctx.defaultRole &&
          ctx.lookupType !== 'ssic')
      ) {
        return {
          messages: [
            {
              role: 'user',
              content: 'Select a **company** from incorporations…',
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'select-entity',
        };
      }
      return {
        messages: [
          {
            role: 'user',
            content: `Looking up person for **${key || 'field'}**…`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          _personTargetKey: key,
          _personFieldGroup: ctx.fieldGroup || 'singbac-incorporation-post',
        },
        nextStepId: ctx.defaultRole ? 'officer-list' : 'officer-role',
        nextStepWidgetData: {
          selectedRole: ctx.defaultRole || undefined,
          role: ctx.defaultRole || undefined,
          entityCd:
            String(ctx.defaultRole || '').toUpperCase() === 'CONTACT'
              ? ''
              : ctx.entityCd || context.formData?.entityCd || '',
          ...(ctx.memberEntityKind ? { memberEntityKind: ctx.memberEntityKind } : {}),
        } as any,
      };
    }

    // 챗봇 커스텀 폼 위젯에서 폼 → 작성(제출) 선택 완료 → 드래그 팔레트 표시(자동바인딩 없음).
    if (eventName === 'custom-form-answer-select') {
      const p = payload || {};
      const answerCd = String(p.customFormAnswerCd || '').trim();
      const formCd = String(p.customFormCd || '').trim();
      if (!answerCd) return {};
      return {
        messages: [
          {
            role: 'user',
            content: `Selected response: **${p._label || answerCd}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          customFormCd: formCd,
          customFormAnswerCd: answerCd,
          customFormLabel: String(p._label || ''),
        },
        nextStepId: 'custom-form-palette',
        nextStepWidgetData: {
          customFormAnswerCd: answerCd,
          customFormCd: formCd,
          customFormLabel: String(p._label || ''),
        } as any,
      };
    }

    if (eventName === 'request-address-input') {
      const key = String(payload?.key || payload || '').trim();
      return {
        messages: [
          {
            role: 'user',
            content: key ? `Enter address for **${key}**.` : 'Enter an address.',
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: { _addressTargetKey: key },
        nextStepId: 'address-input',
      };
    }

    if (eventName === 'officer-role-select') {
      const role = typeof payload === 'string' ? payload : payload?.role || payload?.value;
      if (!role) return {};
      return {
        messages: [{ role: 'user', content: `Role: **${role}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-list',
        nextStepWidgetData: {
          selectedRole: role,
          role,
          entityCd:
            String(role || '').toUpperCase() === 'CONTACT' ? '' : context.formData?.entityCd || '',
        } as any,
      };
    }

    if (eventName === 'officer-select' || eventName === 'person-select') {
      return {
        messages: [{ role: 'user', content: 'Person selected.', timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-detail',
        formUpdates: { _selectedOfficer: payload },
      };
    }

    if (eventName === 'officer-field-apply' || eventName === 'value-submit') {
      if (context.currentStepId === 'officer-detail' || context.currentStepId === 'address-input') {
        return {
          messages: [
            { role: 'user', content: 'Applied to the document.', timestamp: Date.now(), stepId: 1 },
          ],
          nextStepId: 'confirm',
        };
      }
    }

    return {};
  },
};
